import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { FINGERPRINT_HELP, shortFingerprint } from '../../lib/fingerprint.js';
import { marked } from '../../vendor/marked.js';

/**
 * Shows a commission's Statement of Work and captures the viewer's signature.
 *
 * Work is engaged only through an SOW signed by both parties (MSA §1.3): staff
 * sign to open the offer, and the freelancer's countersignature is what assigns
 * the commission and starts its phases. Before Company signs, what is shown is a
 * PREVIEW rendered from the commission — only a stored, signed document is the
 * agreement, so the distinction is on screen rather than implied.
 *
 * An external SOW was signed on paper before the system existed; there is no
 * rendered text for it, only the attached document, so the download link is the
 * whole point of showing it.
 *
 * @property {string} commissionId
 * @property {boolean} staff - whether the viewer signs as Company
 */
class SOWView extends BaseComponent {
    static properties = {
        commissionId: { type: String, attribute: 'commission-id' },
        staff: { type: Boolean },
        _sow: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _signing: { type: Boolean, state: true },
        _voiding: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .sow { border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; }
        .sow-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 0.75rem 1rem; background: #fafafa; border-bottom: 1px solid #eee;
        }
        .sow-header .title { font-weight: 600; }
        .sow-body {
            padding: 1rem 1.25rem; max-height: 28rem; overflow-y: auto;
            font-size: 0.9rem; line-height: 1.55;
        }
        .sow-body h1 { font-size: 1.2rem; margin-top: 0; }
        .sow-body h2 { font-size: 1rem; margin-top: 1.25rem; }
        .sow-footer { padding: 0.75rem 1rem; border-top: 1px solid #eee; text-align: right; }
        .status-badge { font-size: 0.75rem; }
        .preview-note { color: #856404; background: #fff3cd; padding: 0.5rem 1rem; font-size: 0.85rem; }
        .hash { color: #888; font-size: 0.7rem; font-family: monospace; word-break: break-all; }
    `;

    constructor() {
        super();
        this.commissionId = '';
        this.staff = false;
        this._sow = null;
        this._loading = true;
        this._signing = false;
        this._voiding = false;
        this._error = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            this._sow = await api.get(`/api/commissions/${this.commissionId}/sow`);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _sign(path) {
        this._error = null;
        this._signing = true;
        try {
            await api.post(`/api/commissions/${this.commissionId}/sow/${path}`, {});
            await this._load();
            this.dispatchEvent(new CustomEvent('sow-signed', { bubbles: true, composed: true }));
        } catch (err) {
            this._error = err.message;
        } finally {
            this._signing = false;
        }
    }

    async _void() {
        this._error = null;
        this._voiding = true;
        try {
            await api.post(`/api/sows/${this._sow.id}/void`, {});
            await this._load();
            this.dispatchEvent(new CustomEvent('sow-signed', { bubbles: true, composed: true }));
        } catch (err) {
            this._error = err.message;
        } finally {
            this._voiding = false;
        }
    }

    async _download() {
        this._error = null;
        try {
            const { url } = await api.get(`/api/sows/${this._sow.id}/download`);
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            this._error = err.message;
        }
    }

    _renderAction() {
        const status = this._sow?.status;
        if (this.staff && this._sow?.preview) {
            return html`
                <button class="btn btn-primary btn-sm" ?disabled="${this._signing}"
                        @click="${() => this._sign('sign')}">
                    ${this._signing ? 'Signing…' : 'Sign as Company and open the offer'}
                </button>
            `;
        }
        if (!this.staff && status === 'company-signed') {
            return html`
                <button class="btn btn-primary btn-sm" ?disabled="${this._signing}"
                        @click="${() => this._sign('countersign')}">
                    ${this._signing ? 'Signing…' : 'I accept this Statement of Work'}
                </button>
            `;
        }
        if (status === 'external') {
            // Signed on paper: there is no rendered text to show, so the
            // attached document is the only thing worth surfacing.
            return html`
                <span class="d-inline-flex align-items-center gap-2">
                    <span class="text-success status-badge">Signed on paper</span>
                    ${this._renderDownload()}
                </span>
            `;
        }
        if (status === 'executed') {
            return html`
                <span class="d-inline-flex align-items-center gap-2">
                    <span class="text-success status-badge">Signed by both parties</span>
                    ${this._renderDownload()}
                </span>
            `;
        }
        if (status === 'voided') {
            return html`<span class="text-muted status-badge">Voided</span>`;
        }
        if (status === 'terminated') {
            // Signed by both parties, then the freelancer was unassigned: the
            // document stands, the engagement under it is over.
            return html`<span class="text-muted status-badge">Ended</span>`;
        }
        if (status === 'company-signed') {
            return html`
                <span class="d-inline-flex align-items-center gap-2">
                    <span class="text-muted status-badge">Awaiting the freelancer's signature</span>
                    ${
                        this.staff
                            ? html`<button class="btn btn-outline-secondary btn-sm" ?disabled="${this._voiding}"
                                    @click="${() => this._void()}">
                                ${this._voiding ? 'Voiding…' : 'Void'}
                            </button>`
                            : ''
                    }
                </span>
            `;
        }
        return '';
    }

    _renderDownload() {
        if (!this._sow?.has_document) {
            return '';
        }
        return html`
            <button class="btn btn-outline-secondary btn-sm" @click="${() => this._download()}">
                <i class="bi bi-download"></i> Download
            </button>
        `;
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`;
        }
        if (this._error) {
            return html`<div class="alert alert-danger">${this._error}</div>`;
        }
        if (!this._sow) {
            return html`<p class="text-muted">No Statement of Work yet.</p>`;
        }

        return html`
            <div class="sow">
                <div class="sow-header">
                    <span class="title">Statement of Work No. ${this._sow.number ?? '—'}</span>
                    ${this._renderAction()}
                </div>
                ${
                    this._sow.preview
                        ? html`<div class="preview-note">
                    Preview — generated from this commission. It becomes the agreement once signed, and later edits to the commission will not change it.
                </div>`
                        : ''
                }
                ${
                    this._sow.rendered_text
                        ? html`<div class="sow-body" .innerHTML="${marked.parse(this._sow.rendered_text)}"></div>`
                        : html`<div class="sow-body text-muted">
                    Signed outside the system. The signed document is attached — use Download to retrieve it.
                </div>`
                }
                ${
                    this._sow.document_sha256
                        ? html`<div class="sow-footer"><span class="hash" title="${FINGERPRINT_HELP}">Document fingerprint ${shortFingerprint(this._sow.document_sha256)}</span></div>`
                        : ''
                }
            </div>
        `;
    }
}

customElements.define('sow-view', SOWView);
